import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Archive, Download, Loader2 } from "lucide-react";

const fmtBR = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const driveTxt = (d) => (d?.status === "ok" ? "enviado ao Drive" : d?.status === "error" ? `falhou no Drive: ${d.error}` : d?.reason || "");

function FullRow({ it, idx }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/full-backup/download", { params: { filename: it.filename }, responseType: "blob" });
      const u = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = u; a.download = it.filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <li data-testid={`full-backup-row-${idx}`} className="flex items-center gap-3 px-3 py-2 border-t border-zinc-800 first:border-t-0 flex-wrap text-sm">
      <div className="flex-1 min-w-0">
        <div className="text-zinc-100 font-semibold">{fmtBR(it.created_at)} {it.status !== "ok" && <span className="text-red-400">· erro</span>}</div>
        <div className="text-xs text-zinc-500 break-all">
          {it.status === "ok" ? `${it.files} arquivos · ${Object.keys(it.collections || {}).length} coleções · ${driveTxt(it.drive)}` : it.error}
        </div>
      </div>
      {it.status === "ok" && <span className="text-zinc-300 tabular-nums">{fmtSize(it.size)}</span>}
      {it.local && (
        <Button data-testid={`full-backup-download-${idx}`} size="sm" variant="outline" disabled={busy} onClick={download} className="bg-zinc-900 border-zinc-800">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Baixar
        </Button>
      )}
    </li>
  );
}

export function FullBackupCard() {
  const [st, setSt] = useState(null);
  const load = useCallback(() => { api.get("/admin/full-backup").then(({ data }) => setSt(data)).catch(() => setSt({ items: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.running) { return undefined; }
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [st?.running, load]);
  const run = async () => {
    try { await api.post("/admin/full-backup/run"); toast.success("Backup completo iniciado. Pode levar alguns minutos."); setSt((p) => ({ ...(p || {}), running: true })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="full-backup-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-4 md:p-5 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Archive className="h-5 w-5 text-amber-300" />
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Backup completo</h2>
        </div>
        <Button data-testid="full-backup-run" onClick={run} disabled={st?.running} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold uppercase">
          {st?.running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />} {st?.running ? "Gerando..." : "Gerar backup completo agora"}
        </Button>
      </div>
      <p className="text-sm text-zinc-400">Todo domingo às 2h: banco inteiro (todas as oficinas) + fotos, vídeos, logos e anexos, num arquivo compactado. Vai para a pasta "Backup completo" do Google Drive conectado (ficam as 4 últimas). Serve para levar o sistema para outro servidor — veja MIGRACAO.md.</p>
      {st?.items?.length > 0 && (
        <ul data-testid="full-backup-list" className="rounded-lg border border-zinc-800">
          {st.items.map((it, i) => <FullRow key={it.created_at} it={it} idx={i} />)}
        </ul>
      )}
    </div>
  );
}
